import React, {useState} from 'react'
import {useDropzone, DropzoneOptions, FileRejection} from 'react-dropzone'
import styled from 'styled-components'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'

/**
 * List of Document file types.
 */
export const DOCUMENTS = ['image/*', '.pdf', '.xlsx']

/**
 * List of Media file types.
 */
export const MEDIA = ['.mp3', '.mp4', '.mov']

const DEFAULT_MAX_FILE_SIZE_BYTES = 150000000 // bytes

export default function UploadDropzone(props: {
  options?: Omit<DropzoneOptions, 'onDrop'>
  onDrop: (acceptedFile: File) => void
  'aria-label'?: string
}) {
  const [rejectionError, setRejectionError] = useState<string | null>(null)
  const maxSize = props.options?.maxSize ?? DEFAULT_MAX_FILE_SIZE_BYTES

  const handleDrop = (files: File[], rejections: FileRejection[]) => {
    const file = files[0] // Should only receive one file
    if (!file) {
      setRejectionError(rejectionMessage(rejections, maxSize))
      return
    }

    setRejectionError(null)
    props.onDrop(file)
  }

  const {getRootProps, getInputProps} = useDropzone({
    onDrop: handleDrop,
    accept: DOCUMENTS,
    maxSize: DEFAULT_MAX_FILE_SIZE_BYTES,
    maxFiles: 1,
    multiple: false,
    ...props.options,
  })
  const {ref: _, ...rootProps} = getRootProps()

  return (
    <Container>
      <PaperDropzone {...rootProps}>
        <input
          {...getInputProps()}
          aria-label={props['aria-label'] || 'upload single file'}
        />
        <p>Drop a file here or click to upload</p>
      </PaperDropzone>
      {rejectionError && <ErrorText color="error">{rejectionError}</ErrorText>}
    </Container>
  )
}

function rejectionMessage(rejections: FileRejection[], maxSize: number) {
  const hasError = (code: string) =>
    rejections.some((rejection) =>
      rejection.errors.some((error) => error.code === code),
    )

  if (hasError('file-too-large')) {
    return `File is too large. Maximum size is ${formatMegabytes(maxSize)}.`
  }

  if (hasError('file-invalid-type')) {
    return 'File type is not supported.'
  }

  return 'File could not be uploaded.'
}

function formatMegabytes(bytes: number) {
  return `${Math.round(bytes / 1000000)}MB`
}

const Container = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[6]};
`

const PaperDropzone = styled(Paper)`
  cursor: pointer;
  padding: ${(props) => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: center;

  > p {
    margin: 0;
  }
`

const ErrorText = styled(Typography)`
  margin-top: ${(props) => props.theme.spacing[2]};
`
